/**
 * Local Storage Serce for character data persistence
 * Handles save/load/delete operations with error handling and backup mechanisms
 */

import type { Character } from '../types/character';
import {
  SaveResult,
  LoadResult,
  BackupMetadata,
  StorageLocation,
  Summary
} from '../types/persistence';
import { validateCharacter } from '../types/validation';
import { calculateChecksum } from '../utils/checksum';

// Storage keys
const CHARACTER_STORAGE_KEY = 'rogue-trader-character';
const CHARACTER_LIST_KEY = 'rogue-trader-characters';
const BACKUP_PREFIX = 'rogue-trader-backup-';
const SETTINGS_KEY = 'rogue-trader-settings';

// Storage limits and configuration
const MAX_BACKUP_COUNT = 5;
const STORAGE_QUOTA_THRESHOLD = 0.9; // 90% of quota
const COMPRESSION_THRESHOLD = 50000; // 50KB - compress larger data

interface StoredManualAdjustment {
  id: string;
  timestamp: string; // stored as string, converted to Date
  field: string;
  oldValue: unknown;
  newValue: unknown;
  reason: string;
}

interface StoredSummary {
  id: string;
  name: string;
  career: string;
  level: number;
  lastModified: string; // stored as string, converted to Date
}

interface StorageSettings {
  autoBackup: boolean;
  maxBackups: number;
  compressionEnabled: boolean;
}

interface Serialized {
  character: unknown;
  metadata: {
    version: number;
    timestamp: string;
    checksum: string;
    compressed: boolean;
  };
}

interface StorageInfo {
  used: number;
  available: number;
  quota: number;
  usagePercentage: number;
}

export class LocalStorageService {
  private settings: StorageSettings;

  constructor() {
    this.settings = this.loadSettings();
  }

  /**
   * Save character to localStorage with backup and error handling
   */
  async save(character: Character): Promise<SaveResult> {
    const startTime = Date.now();

    try {
      // Validate character data before saving
      const validation = validateCharacter(character);
      if (!validation.success) {
        return {
          success: false,
          location: StorageLocation.Local,
          timestamp: new Date(),
          error: `validation failed: ${validation.errors.join(', ')}`
        };
      }

      // Check storage quota before sang
      const storageInfo = this.getStorageInfo();
      if (storageInfo.usagePercentage > STORAGE_QUOTA_THRESHOLD) {
        // Try to free up space by remong old backups
        await this.cleanupOldBackups();

        // Check again after cleanup
        const updatedStorageInfo = this.getStorageInfo();
        if (updatedStorageInfo.usagePercentage > STORAGE_QUOTA_THRESHOLD) {
          return {
            success: false,
            location: StorageLocation.Local,
            timestamp: new Date(),
            error: 'Storage quota exceeded. Please free up space or export your character data.'
          };
        }
      }

      // Create backup if enabled
      if (this.settings.autoBackup) {
        await this.createBackup(character);
      }

      // Serialize character data
      const serializedData = this.serialize(character);

      // Save to localStorage
      localStorage.setItem(CHARACTER_STORAGE_KEY, JSON.stringify(serializedData));

      // Update character list
      await this.updateList(character);

      const endTime = Date.now();
      console.log(`saved to localStorage in ${endTime - startTime}ms`);

      return {
        success: true,
        location: StorageLocation.Local,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Failed to save character to localStorage:', error);

      // Handle specific storage errors
      if (error instanceof DOMException) {
        if (error.name === 'QuotaExceededError') {
          return {
            success: false,
            location: StorageLocation.Local,
            timestamp: new Date(),
            error: 'Storage quota exceeded. Please free up space or use cloud storage.'
          };
        } else if (error.name === 'SecurityError') {
          return {
            success: false,
            location: StorageLocation.Local,
            timestamp: new Date(),
            error: 'Storage access denied. Please check browser settings.'
          };
        }
      }

      return {
        success: false,
        location: StorageLocation.Local,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown storage error'
      };
    }
  }

  /**
   * Load character from localStorage with corruption detection
   */
  async load(_characterId?: string): Promise<LoadResult> {
    const startTime = Date.now();

    try {
      const savedData = localStorage.getItem(CHARACTER_STORAGE_KEY);
      if (!savedData) {
        return {
          success: false,
          location: StorageLocation.Local,
          timestamp: new Date(),
          error: 'No character data found in storage'
        };
      }

      // Parse and validate serialized data
      let serializedData: Serialized;
      try {
        serializedData = JSON.parse(savedData);
      } catch (parseError) {
        console.error('Failed to parse character data:', parseError);

        // Try to recover from backup
        const backupResult = await this.recoverFromBackup();
        if (backupResult.success && backupResult.character) {
          return backupResult;
        }

        return {
          success: false,
          location: StorageLocation.Local,
          timestamp: new Date(),
          error: 'data is corrupted and no backup is available'
        };
      }

      // Verify data integrity
      const integrityCheck = this.verifyDataIntegrity(serializedData);
      if (!integrityCheck.valid) {
        console.warn('Data integrity check failed:', integrityCheck.error);

        // Try to recover from backup
        const backupResult = await this.recoverFromBackup();
        if (backupResult.success && backupResult.character) {
          return backupResult;
        }

        return {
          success: false,
          location: StorageLocation.Local,
          timestamp: new Date(),
          error: `Data corruption detected: ${integrityCheck.error}`
        };
      }

      // Deserialize character data
      const character = this.deserialize(serializedData);

      // Validate deserialized character
      const validation = validateCharacter(character);
      if (!validation.success) {
        console.warn('Loaded character failed validation:', validation.errors);
        // Continue loading but with warnings
      }

      const endTime = Date.now();
      console.log(`loaded from localStorage in ${endTime - startTime}ms`);

      return {
        success: true,
        character,
        location: StorageLocation.Local,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Failed to load character from localStorage:', error);

      return {
        success: false,
        location: StorageLocation.Local,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown loading error'
      };
    }
  }

  /**
   * Delete character from localStorage
   */
  async delete(characterId: string): Promise<SaveResult> {
    try {
      // Remove main character data
      localStorage.removeItem(CHARACTER_STORAGE_KEY);

      // Remove from character list
      const characterList = this.getList();
      const updatedList = characterList.filter((char) => char.id !== characterId);
      localStorage.setItem(CHARACTER_LIST_KEY, JSON.stringify(updatedList));

      // Remove associated backups
      await this.deleteBackups(characterId);

      return {
        success: true,
        location: StorageLocation.Local,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Failed to delete character from localStorage:', error);

      return {
        success: false,
        location: StorageLocation.Local,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown deletion error'
      };
    }
  }

  /**
   * List all characters in localStorage
   */
  async lists(): Promise<Summary[]> {
    try {
      return this.getList();
    } catch (error) {
      console.error('Failed to list characters:', error);
      return [];
    }
  }

  /**
   * Create backup of character data
   */
  async createBackup(character: Character): Promise<BackupMetadata | null> {
    try {
      const backupId = `${character.id}-${Date.now()}`;
      const backupKey = `${BACKUP_PREFIX}${backupId}`;

      const serializedData = this.serialize(character);
      const backupData = JSON.stringify(serializedData);

      localStorage.setItem(backupKey, backupData);

      const metadata: BackupMetadata = {
        id: backupId,
        characterId: character.id,
        timestamp: new Date(),
        version: character.schemaVersion,
        size: backupData.length,
        location: StorageLocation.Local
      };

      // Update backup list
      await this.updateBackupList(metadata);

      // Cleanup old backups if needed
      await this.cleanupOldBackups();

      return metadata;
    } catch (error) {
      console.error('Failed to create backup:', error);
      return null;
    }
  }

  /**
   * Recover character from most recent backup
   */
  async recoverFromBackup(): Promise<LoadResult> {
    try {
      const backups = await this.listBackups();
      if (backups.length === 0) {
        return {
          success: false,
          location: StorageLocation.Local,
          timestamp: new Date(),
          error: 'No backups available for recovery'
        };
      }

      // Get most recent backup
      const latestBackup = backups.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];

      const backupKey = `${BACKUP_PREFIX}${latestBackup.id}`;
      const backupData = localStorage.getItem(backupKey);

      if (!backupData) {
        return {
          success: false,
          location: StorageLocation.Local,
          timestamp: new Date(),
          error: 'Backup data not found'
        };
      }

      const serializedData: Serialized = JSON.parse(backupData);
      const character = this.deserialize(serializedData);

      console.log(`recovered from backup: ${latestBackup.id}`);

      return {
        success: true,
        character,
        location: StorageLocation.Local,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Failed to recover from backup:', error);

      return {
        success: false,
        location: StorageLocation.Local,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Backup recovery failed'
      };
    }
  }

  /**
   * List all available backups
   */
  async listBackups(): Promise<BackupMetadata[]> {
    try {
      const backups: BackupMetadata[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(BACKUP_PREFIX)) {
          const backupData = localStorage.getItem(key);
          if (backupData) {
            try {
              const serializedData: Serialized = JSON.parse(backupData);
              const backupId = key.replace(BACKUP_PREFIX, '');
              const parts = backupId.split('-');
              const timestamp = parts[parts.length - 1];
              const characterId = parts.slice(0, -1).join('-');

              backups.push({
                id: backupId,
                characterId,
                timestamp: new Date(parseInt(timestamp)),
                version: serializedData.metadata.version,
                size: backupData.length,
                location: StorageLocation.Local
              });
            } catch (error) {
              console.warn(`Failed to parse backup ${key}:`, error);
            }
          }
        }
      }

      return backups.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    } catch (error) {
      console.error('Failed to list backups:', error);
      return [];
    }
  }

  /**
   * Get storage usage information
   */
  getStorageInfo(): StorageInfo {
    let used = 0;

    // Calculate used storage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          used += key.length + value.length;
        }
      }
    }

    // Estimate quota (browsers typically allow 5-10MB for localStorage)
    const quota = 10 * 1024 * 1024; // 10MB estimate
    const available = quota - used;
    const usagePercentage = used / quota;

    return {
      used,
      available,
      quota,
      usagePercentage
    };
  }

  /**
   * Clear all character data and backups
   */
  async clearAllData(): Promise<void> {
    try {
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key === CHARACTER_STORAGE_KEY ||
            key === CHARACTER_LIST_KEY ||
            key.startsWith(BACKUP_PREFIX))
        ) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key));

      console.log('All character data cleared from localStorage');
    } catch (error) {
      console.error('Failed to clear all data:', error);
      throw error;
    }
  }

  // Private helper methods

  private serialize(character: Character): Serialized {
    // Serialize progression data Date objects if progression exists
    let progressionData: any = character.progression;
    if (progressionData) {
      progressionData = {
        ...progressionData,
        mutationTracking: {
          ...progressionData.mutationTracking,
          mutations: progressionData.mutationTracking.mutations.map((mut: any) => ({
            ...mut,
            acquiredAt:
              mut.acquiredAt instanceof Date ? mut.acquiredAt.toISOString() : mut.acquiredAt
          }))
        },
        woundsTracking: {
          ...progressionData.woundsTracking,
          criticalDamage: progressionData.woundsTracking.criticalDamage.map((crit: any) => ({
            ...crit,
            timestamp:
              crit.timestamp instanceof Date ? crit.timestamp.toISOString() : crit.timestamp
          })),
          permanentInjuries: progressionData.woundsTracking.permanentInjuries.map((inj: any) => ({
            ...inj,
            acquiredAt:
              inj.acquiredAt instanceof Date ? inj.acquiredAt.toISOString() : inj.acquiredAt
          }))
        }
      };
    }

    // Serialize rankTracking data Date objects if rankTracking exists
    // Requirements: 5.5, 6.1, 8.1 - Persist rank selection state
    let rankTrackingData: any = character.rankTracking;
    if (rankTrackingData) {
      rankTrackingData = {
        ...rankTrackingData,
        rankHistory: rankTrackingData.rankHistory.map((entry: any) => ({
          ...entry,
          achievedAt:
            entry.achievedAt instanceof Date ? entry.achievedAt.toISOString() : entry.achievedAt
        }))
      };
    }

    const characterData = {
      ...character,
      createdAt: character.createdAt.toISOString(),
      lastModified: character.lastModified.toISOString(),
      manualAdjustments: character.manualAdjustments.map((adj) => ({
        ...adj,
        timestamp: adj.timestamp.toISOString()
      })),
      ...(progressionData && { progression: progressionData }),
      ...(rankTrackingData && { rankTracking: rankTrackingData })
    };

    const serialized = JSON.stringify(characterData);
    const checksum = calculateChecksum(serialized);
    const compressed =
      this.settings.compressionEnabled && serialized.length > COMPRESSION_THRESHOLD;

    return {
      character: characterData,
      metadata: {
        version: 1,
        timestamp: new Date().toISOString(),
        checksum,
        compressed
      }
    };
  }

  private deserialize(serializedData: Serialized): Character {
    const characterData = serializedData.character as Character;

    // Deserialize progression data Date objects if progression exists
    let progressionData = characterData.progression;
    if (progressionData) {
      progressionData = {
        ...progressionData,
        mutationTracking: {
          ...progressionData.mutationTracking,
          mutations: progressionData.mutationTracking.mutations.map((mut: any) => {
            return {
              ...mut,
              acquiredAt: new Date(mut.acquiredAt)
            };
          })
        },
        woundsTracking: {
          ...progressionData.woundsTracking,
          criticalDamage: progressionData.woundsTracking.criticalDamage.map((crit: any) => {
            return {
              ...crit,
              timestamp: new Date(crit.timestamp)
            };
          }),
          permanentInjuries: progressionData.woundsTracking.permanentInjuries.map((inj: any) => {
            return {
              ...inj,
              acquiredAt: new Date(inj.acquiredAt)
            };
          })
        }
      };
    }

    // Deserialize rankTracking data Date objects if rankTracking exists
    // Requirements: 5.5, 6.1, 8.1 - Restore rank selection state
    let rankTrackingData = characterData.rankTracking;
    if (rankTrackingData) {
      rankTrackingData = {
        ...rankTrackingData,
        rankHistory: rankTrackingData.rankHistory.map((entry: any) => {
          return {
            ...entry,
            achievedAt: new Date(entry.achievedAt)
          };
        })
      };
    }

    return {
      ...characterData,
      createdAt: new Date(characterData.createdAt),
      lastModified: new Date(characterData.lastModified),
      manualAdjustments: characterData.manualAdjustments.map((adj: unknown) => {
        const storedAdj = adj as StoredManualAdjustment;
        return {
          ...storedAdj,
          timestamp: new Date(storedAdj.timestamp)
        };
      }),
      ...(progressionData && { progression: progressionData }),
      ...(rankTrackingData && { rankTracking: rankTrackingData })
    };
  }

  private verifyDataIntegrity(serializedData: Serialized): { valid: boolean; error?: string } {
    try {
      // Check if metadata exists
      if (!serializedData.metadata) {
        return { valid: false, error: 'Missing metadata' };
      }

      // Verify checksum
      const currentChecksum = calculateChecksum(JSON.stringify(serializedData.character));
      if (currentChecksum !== serializedData.metadata.checksum) {
        return { valid: false, error: 'Checksum mismatch - data may be corrupted' };
      }

      // Check version compatibility
      if (serializedData.metadata.version > 1) {
        return { valid: false, error: 'Unsupported data version' };
      }

      return { valid: true };
    } catch (error) {
      return { valid: false, error: 'Integrity check failed' };
    }
  }

  private getList(): Summary[] {
    try {
      const listData = localStorage.getItem(CHARACTER_LIST_KEY);
      if (!listData) return [];

      const list = JSON.parse(listData);
      return list.map((item: StoredSummary) => ({
        ...item,
        lastModified: new Date(item.lastModified)
      }));
    } catch (error) {
      console.error('Failed to get character list:', error);
      return [];
    }
  }

  private async updateList(character: Character): Promise<void> {
    try {
      const characterList = this.getList();
      const existingIndex = characterList.findIndex((char) => char.id === character.id);

      const summary: Summary = {
        id: character.id,
        name: character.name,
        career: character.career.name || 'Unknown',
        level: Math.floor(character.experience.spent / 100) + 1,
        lastModified: character.lastModified,
        location: StorageLocation.Local,
        syncStatus: {
          synced: true,
          pending: false,
          conflict: false,
          error: false,
          offline: false
        }
      };

      if (existingIndex >= 0) {
        characterList[existingIndex] = summary;
      } else {
        characterList.push(summary);
      }

      localStorage.setItem(CHARACTER_LIST_KEY, JSON.stringify(characterList));
    } catch (error) {
      console.error('Failed to update character list:', error);
    }
  }

  private async updateBackupList(_metadata: BackupMetadata): Promise<void> {
    // This could be implemented to maintain a backup index if needed
    // For now, we discover backups by scanning localStorage keys
  }

  private async cleanupOldBackups(): Promise<void> {
    try {
      const backups = await this.listBackups();

      if (backups.length > this.settings.maxBackups) {
        // Sort by timestamp and remove oldest backups
        const sortedBackups = backups.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        const backupsToRemove = sortedBackups.slice(0, backups.length - this.settings.maxBackups);

        for (const backup of backupsToRemove) {
          const backupKey = `${BACKUP_PREFIX}${backup.id}`;
          localStorage.removeItem(backupKey);
        }

        console.log(`Cleaned up ${backupsToRemove.length} old backups`);
      }
    } catch (error) {
      console.error('Failed to cleanup old backups:', error);
    }
  }

  private async deleteBackups(characterId: string): Promise<void> {
    try {
      const backups = await this.listBackups();
      const characterBackups = backups.filter((backup) => backup.characterId === characterId);

      for (const backup of characterBackups) {
        const backupKey = `${BACKUP_PREFIX}${backup.id}`;
        localStorage.removeItem(backupKey);
      }

      console.log(`Deleted ${characterBackups.length} backups for character ${characterId}`);
    } catch (error) {
      console.error('Failed to delete character backups:', error);
    }
  }

  private loadSettings(): StorageSettings {
    try {
      const settingsData = localStorage.getItem(SETTINGS_KEY);
      if (settingsData) {
        const settings = JSON.parse(settingsData);
        return {
          autoBackup: settings.autoBackup ?? true,
          maxBackups: settings.maxBackups ?? MAX_BACKUP_COUNT,
          compressionEnabled: settings.compressionEnabled ?? false
        };
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }

    // Return default settings
    return {
      autoBackup: true,
      maxBackups: MAX_BACKUP_COUNT,
      compressionEnabled: false
    };
  }

  /**
   * Update serce settings
   */
  updateSettings(newSettings: Partial<StorageSettings>): void {
    this.settings = { ...this.settings, ...newSettings };

    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.settings));
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }
}

// Export singleton instance
export const localStorageService = new LocalStorageService();
